import { useMemo, useState } from 'react';
import { chileTerritory } from '../data/chileTerritory';
import { extractTags, addTagToField } from '../utils/tags';

function ActorForm({
  newActor,
  setNewActor,
  guardarActor,
  editingActorId,
  cancelarEdicionActor,
  allActors = []
}) {
  
  const [nuevaNarrativa, setNuevaNarrativa] = useState('');

  const suggestedTags = useMemo(() => extractTags(allActors), [allActors]);
  const regiones = useMemo(() => chileTerritory.map((item) => item.region), []);

  const comunasDisponibles = useMemo(() => {
    const selectedRegion = chileTerritory.find(
      (item) => item.region === newActor.region
    );
    return selectedRegion ? selectedRegion.comunas : [];
  }, [newActor.region]);

  const sectoresSugeridos = useMemo(() => {
    return Array.from(
      new Set(
        allActors
          .filter((actor) => !newActor.comuna || actor.comuna === newActor.comuna)
          .map((actor) => actor.sector)
          .filter(Boolean)
      )
    ).sort();
  }, [allActors, newActor.comuna]);

  const focosSugeridos = useMemo(() => {
    return Array.from(
      new Set(allActors.map((actor) => actor.foco).filter(Boolean))
    ).sort();
  }, [allActors]);

  const updateField = (field, value) => {
    setNewActor({ ...newActor, [field]: value });
  };

  const agregarTag = (tag) => {
    setNewActor({ ...newActor, tags: addTagToField(newActor.tags, tag) });
  };

  // Narrativas como array
  const narrativas = Array.isArray(newActor.narrativas) ? newActor.narrativas : [];

  const agregarNarrativa = () => {
    const texto = nuevaNarrativa.trim();
    if (!texto) return;
    if (narrativas.includes(texto)) return;
    setNewActor({ ...newActor, narrativas: [...narrativas, texto] });
    setNuevaNarrativa('');
  };

  const eliminarNarrativa = (index) => {
    const nuevas = narrativas.filter((_, i) => i !== index);
    setNewActor({ ...newActor, narrativas: nuevas });
  };

  return (
    <div style={styles.card}>
      <h2 style={styles.mainTitle}>
        {editingActorId ? 'Editar stakeholder' : 'Nuevo stakeholder'}
      </h2>

      {/* ── SECCIÓN ESENCIAL ── */}
      <div style={styles.sectionHeader}>
        <h3 style={styles.sectionTitle}>Datos esenciales</h3>
        <span style={styles.sectionNote}>
          Campos mínimos para activar el motor cognitivo
        </span>
      </div>

      <div style={styles.grid}>
        <Input label="Nombre *" field="nombre" actor={newActor} updateField={updateField} span={2} />
        <Input label="Tipo de actor" field="tipo" actor={newActor} updateField={updateField} />
        <Select
          label="Estado actual *"
          field="estado"
          value={newActor.estado}
          updateField={updateField}
          options={['Positivo', 'Neutral', 'Crítico']}
        />
        <Select
          label="Riesgo actual *"
          field="riesgo"
          value={newActor.riesgo}
          updateField={updateField}
          options={['Bajo', 'Medio', 'Alto', 'Crítico']}
        />
        <Select
          label="Influencia *"
          field="influencia"
          value={newActor.influencia}
          updateField={updateField}
          options={['Baja', 'Media', 'Alta']}
        />
      </div>

      {/* Ubicación */}
      <div style={styles.grid}>
        <select
          style={styles.input}
          value={newActor.region || ''}
          onChange={(e) => setNewActor({ ...newActor, region: e.target.value, comuna: '' })}
        >
          <option value="">Región *</option>
          {regiones.map((region) => (
            <option key={region} value={region}>{region}</option>
          ))}
        </select>

        <select
          style={styles.input}
          value={newActor.comuna || ''}
          disabled={!newActor.region}
          onChange={(e) => updateField('comuna', e.target.value)}
        >
          <option value="">
            {newActor.region ? 'Comuna *' : 'Selecciona región primero'}
          </option>
          {comunasDisponibles.map((comuna) => (
            <option key={comuna} value={comuna}>{comuna}</option>
          ))}
        </select>

        <input
          style={styles.input}
          placeholder="Sector"
          list="sectores-sugeridos"
          value={newActor.sector || ''}
          onChange={(e) => updateField('sector', e.target.value)}
        />
        <datalist id="sectores-sugeridos">
          {sectoresSugeridos.map((sector) => (
            <option key={sector} value={sector} />
          ))}
        </datalist>

        <input
          style={styles.input}
          placeholder="Foco territorial *"
          list="focos-sugeridos"
          value={newActor.foco || ''}
          onChange={(e) => updateField('foco', e.target.value)}
        />
        <datalist id="focos-sugeridos">
          {focosSugeridos.map((foco) => (
            <option key={foco} value={foco} />
          ))}
        </datalist>

        <Input label="Latitud" field="latitud" actor={newActor} updateField={updateField} />
        <Input label="Longitud" field="longitud" actor={newActor} updateField={updateField} />
      </div>

      {/* ── NARRATIVAS ── */}
      <div style={styles.narrativasBox}>
        <div style={styles.narrativasHeader}>
          <div>
            <div style={styles.narrativasLabel}>Narrativas territoriales</div>
            <div style={styles.narrativasNota}>
              Lo que este actor dice, repite o difunde. Cada frase que escuchas en terreno, reuniones o RRSS.
            </div>
          </div>
        </div>

        {/* Lista de narrativas agregadas */}
        {narrativas.length > 0 && (
          <div style={styles.narrativasList}>
            {narrativas.map((narrativa, index) => (
              <div key={index} style={styles.narrativaItem}>
                <span style={styles.narrativaTexto}>"{narrativa}"</span>
                <button
                  style={styles.narrativaEliminar}
                  onClick={() => eliminarNarrativa(index)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Input para agregar nueva narrativa */}
        <div style={styles.narrativaInputRow}>
          <input
            style={styles.narrativaInput}
            placeholder='Ej: "El embalse secará los pozos" · "No nos consultaron" · "El agua es para Santiago"'
            value={nuevaNarrativa}
            onChange={(e) => setNuevaNarrativa(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                agregarNarrativa();
              }
            }}
          />
          <button style={styles.narrativaAgregar} onClick={agregarNarrativa}>
            + Agregar
          </button>
        </div>
      </div>

      {/* Tags */}
      <div style={styles.grid}>
        <Input
          label="Etiquetas (separadas por coma)"
          field="tags"
          actor={newActor}
          updateField={updateField}
          span={3}
        />
      </div>

      {suggestedTags.length > 0 && (
        <div style={styles.suggestedBox}>
          <div style={styles.suggestedTitle}>Etiquetas sugeridas</div>
          <div style={styles.tagsBox}>
            {suggestedTags.map((tag) => (
              <button
                key={tag}
                style={styles.suggestedTag}
                onClick={() => agregarTag(tag)}
              >
                #{tag}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Observaciones */}
      <textarea
        style={styles.textarea}
        rows="4"
        placeholder="Observaciones territoriales — contexto, antecedentes, percepciones relevantes"
        value={newActor.observaciones || ''}
        onChange={(e) => updateField('observaciones', e.target.value)}
      />

      <div style={styles.sectionHeader}>
        <h3 style={styles.sectionTitle}>Contacto y monitoreo</h3>
      </div>
      <div style={styles.grid}>
        <Input label="Teléfono" field="telefono" actor={newActor} updateField={updateField} />
        <Input label="Correo" field="correo" actor={newActor} updateField={updateField} />
        <Input label="WhatsApp" field="whatsapp" actor={newActor} updateField={updateField} />
        <Input label="Sitio web" field="sitioWeb" actor={newActor} updateField={updateField} />
        <Input label="Facebook" field="facebook" actor={newActor} updateField={updateField} />
        <Input label="Instagram" field="instagram" actor={newActor} updateField={updateField} />
        <Input label="X / Twitter" field="xTwitter" actor={newActor} updateField={updateField} />
        <Input label="LinkedIn" field="linkedin" actor={newActor} updateField={updateField} />
      </div>

      <div style={styles.sectionHeader}>
        <h3 style={styles.sectionTitle}>Capacidad territorial</h3>
        <span style={styles.sectionNote}>El engine los infiere si están vacíos</span>
      </div>
      <div style={styles.grid}>
        <LevelSelect label="Sensibilidad territorial" field="sensibilidadTerritorial" value={newActor.sensibilidadTerritorial} updateField={updateField} />
        <LevelSelect label="Exposición territorial" field="exposicionTerritorial" value={newActor.exposicionTerritorial} updateField={updateField} />
        <LevelSelect label="Legitimidad territorial" field="legitimidadTerritorial" value={newActor.legitimidadTerritorial} updateField={updateField} />
        <LevelSelect label="Capacidad de movilización" field="capacidadMovilizacion" value={newActor.capacidadMovilizacion} updateField={updateField} />
        <LevelSelect label="Capacidad de propagación" field="capacidadPropagacion" value={newActor.capacidadPropagacion} updateField={updateField} />
        <LevelSelect label="Historial territorial" field="historialTerritorial" value={newActor.historialTerritorial} updateField={updateField} />
      </div>

      <div style={styles.actions}>
        <button style={styles.button} onClick={guardarActor}>
          {editingActorId ? 'Guardar cambios' : 'Guardar stakeholder'}
        </button>
        {editingActorId && (
          <button style={styles.cancelButton} onClick={cancelarEdicionActor}>
            Cancelar
          </button>
        )}
      </div>
    </div>
  );
}

function Input({ label, field, actor, updateField, span }) {
  return (
    <input
      style={{
        ...styles.input,
        ...(span ? { gridColumn: `span ${span}` } : {})
      }}
      placeholder={label}
      value={actor[field] || ''}
      onChange={(e) => updateField(field, e.target.value)}
    />
  );
}

function Select({ label, field, value, updateField, options }) {
  return (
    <select
      style={styles.input}
      value={value || ''}
      onChange={(e) => updateField(field, e.target.value)}
    >
      <option value="">{label}</option>
      {options.map((option) => (
        <option key={option} value={option}>{option}</option>
      ))}
    </select>
  );
}

function LevelSelect({ label, field, value, updateField }) {
  return (
    <select
      style={styles.input}
      value={value || ''}
      onChange={(e) => updateField(field, e.target.value)}
    >
      <option value="">{label}</option>
      <option value="Baja">Baja</option>
      <option value="Media">Media</option>
      <option value="Alta">Alta</option>
      <option value="Crítica">Crítica</option>
    </select>
  );
}

const styles = {
  card: {
    background: 'white',
    padding: '30px',
    borderRadius: '14px',
    marginBottom: '30px',
    boxShadow: '0 8px 25px rgba(0,0,0,0.06)'
  },
  mainTitle: {
    marginTop: 0,
    color: '#071a2f',
    fontSize: '24px'
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '12px',
    marginTop: '28px',
    marginBottom: '14px',
    borderBottom: '1px solid #e4e7ec',
    paddingBottom: '8px'
  },
  sectionTitle: {
    margin: 0,
    color: '#071a2f',
    fontSize: '17px'
  },
  sectionNote: {
    color: '#98a2b3',
    fontSize: '12px'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '14px',
    marginBottom: '16px'
  },
  input: {
    padding: '14px',
    borderRadius: '10px',
    border: '1px solid #d0d5dd',
    fontSize: '14px'
  },
  textarea: {
    width: '100%',
    padding: '16px',
    borderRadius: '10px',
    border: '1px solid #d0d5dd',
    marginBottom: '16px',
    boxSizing: 'border-box',
    fontSize: '14px',
    lineHeight: '1.5'
  },
  narrativasBox: {
    background: '#f0f9ff',
    border: '1px solid #bae6fd',
    borderRadius: '14px',
    padding: '18px',
    marginBottom: '16px'
  },
  narrativasHeader: {
    marginBottom: '14px'
  },
  narrativasLabel: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#075985',
    marginBottom: '4px'
  },
  narrativasNota: {
    fontSize: '12px',
    color: '#64748b',
    lineHeight: '1.5'
  },
  narrativasList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '12px'
  },
  narrativaItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'white',
    border: '1px solid #bae6fd',
    borderRadius: '10px',
    padding: '10px 14px',
    gap: '12px'
  },
  narrativaTexto: {
    color: '#0c4a6e',
    fontSize: '14px',
    lineHeight: '1.5',
    flex: 1
  },
  narrativaEliminar: {
    background: 'rgba(239,68,68,0.1)',
    border: 'none',
    color: '#dc2626',
    borderRadius: '6px',
    width: '28px',
    height: '28px',
    cursor: 'pointer',
    fontSize: '18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  narrativaInputRow: {
    display: 'flex',
    gap: '10px'
  },
  narrativaInput: {
    flex: 1,
    padding: '12px 14px',
    borderRadius: '10px',
    border: '1px solid #bae6fd',
    fontSize: '14px',
    background: 'white'
  },
  narrativaAgregar: {
    background: '#0891b2',
    color: 'white',
    border: 'none',
    padding: '12px 18px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
    whiteSpace: 'nowrap'
  },
  toggleAvanzado: {
    background: '#f8fafc',
    border: '1px solid #e4e7ec',
    borderRadius: '10px',
    padding: '12px 18px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#344054',
    width: '100%',
    textAlign: 'left',
    marginBottom: '8px'
  },
  toggleNota: {
    fontWeight: 'normal',
    color: '#98a2b3',
    fontSize: '12px'
  },
  suggestedBox: {
    background: '#f8fafc',
    border: '1px solid #e4e7ec',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '16px'
  },
  suggestedTitle: {
    fontWeight: 'bold',
    marginBottom: '12px',
    fontSize: '13px',
    color: '#344054'
  },
  tagsBox: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px'
  },
  suggestedTag: {
    background: '#e0f2fe',
    color: '#075985',
    border: 'none',
    padding: '6px 12px',
    borderRadius: '999px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  button: {
    background: '#0b1f2e',
    color: 'white',
    border: 'none',
    padding: '14px 22px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: 'bold'
  },
  cancelButton: {
    background: '#d92d20',
    color: 'white',
    border: 'none',
    padding: '14px 22px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: 'bold'
  },
  actions: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px'
  }
};

export default ActorForm;